import React from 'react'
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { Button } from '@mui/material'
import PowerMeterForm from 'src/Libs/installations/components/new-installation/configuration/power-meters/PowerMeterForm'
import { usePortalMutation } from 'src/Libs/api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import useModals from 'src/Hooks/useModals'
import { deletePlannedPowerMeter } from 'src/Libs/installations/api'
import { PlannedPowerMeterModel } from 'src/Libs/installations/models'

interface PowerMeterTileProps {
  tileKey: string
  setNewPowerMeter: React.Dispatch<React.SetStateAction<boolean>>
  emcDeviceLinkId: string | null
  emcSerialnumber: string | null
  allPowerMeters: PlannedPowerMeterModel[]
  installationId?: number
  refetchSubDevices: (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<unknown, Error>>
  powerMeter?: PlannedPowerMeterModel
}

interface DeletePowerMeterVariables {
  installationId: number
  emcDeviceLinkId: string
  powerMeter: PlannedPowerMeterModel
}

const PowerMeterTile: React.FC<PowerMeterTileProps> = ({
  tileKey,
  setNewPowerMeter,
  emcDeviceLinkId,
  emcSerialnumber,
  allPowerMeters,
  installationId,
  refetchSubDevices,
  powerMeter,
}) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { openConfirmationModal } = useModals()

  const deletePowerMeterMutation = usePortalMutation<null, unknown, DeletePowerMeterVariables>({
    mutationFn: ({ installationId, emcDeviceLinkId, powerMeter }) => deletePlannedPowerMeter(installationId, emcDeviceLinkId, powerMeter, authFetch),
    onSuccess: () => {
      enqueueSuccessStack(t('installation-configuration.general-settings.powermeter.delete.success'))
      void refetchSubDevices()
    },
    onError: error => {
      enqueueErrorStack(t('installation-configuration.general-settings.powermeter.delete.error'))
      console.error('error deleting power meter', error)
    },
  })

  const handleDelete = (): void => {
    if (!powerMeter) {
      setNewPowerMeter(false)
      return
    }

    if (!installationId || !emcDeviceLinkId) {
      enqueueErrorStack(t('installation-configuration.general-settings.save.error'))
      return
    }

    deletePowerMeterMutation.mutate({
      installationId,
      emcDeviceLinkId,
      powerMeter,
    })
  }

  return (
    <AccordionItem
      title={powerMeter?.deviceName ?? t('installation-configuration.power-meters.new-powermeter')}
      expanded={true}
      headerChildren={
        <Button
          onClick={() =>
            openConfirmationModal({
              title: t('installation-configuration.general-settings.powermeter.delete.confirmation.title'),
              text: t('installation-configuration.general-settings.powermeter.delete.confirmation.description'),
              buttonText: [t('common.cancel'), t('common.delete')],
              onConfirm: handleDelete,
            })
          }
          variant={'text'}
        >
          {t('common.delete')}
        </Button>
      }
      accordionDetailsSx={{ px: 4, py: 2 }}
      expandOnlyOnIcon
    >
      <PowerMeterForm
        tileKey={tileKey}
        installationId={installationId}
        emcSerialnumber={emcSerialnumber}
        emcDeviceLinkId={emcDeviceLinkId}
        allPowermeters={allPowerMeters}
        powermeter={powerMeter}
        onSuccess={() => {
          void refetchSubDevices()
          setNewPowerMeter(false)
        }}
      />
    </AccordionItem>
  )
}

export default PowerMeterTile
